/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { CosmosRichText, CosmosText } from "@components/elements/atoms";
import { css, cx } from "@panda/css";
import { Box, VStack } from "@panda/jsx";

import {
	EulaAgreementFooter,
	EulaAgreementFooterExtraProps,
	EulaAgreementFooterProps,
} from "./EulaAgreementFooter.molecule";

export interface EulaAgreementDetailsMainProps extends EulaAgreementFooterProps {
	title?: string | null;
	eulaContentTitle?: string | null;
	eulaContentBody?: string | null;
}
export type EulaAgreementDetailsProps = EulaAgreementDetailsMainProps & EulaAgreementFooterExtraProps;

const overflowStyles = css({
	overflowX: "auto",
	maxWidth: "100%",
	smDown: {
		padding: "eds.dimension.16",
	},
	padding: "eds.dimension.24",
	"--scrollbar-track-border-width": "token(sizes.eds.dimension.1)",
	"--scrollbar-thumb-width": "token(sizes.eds.dimension.6)",
	"--scrollbar-thumb-padding": "token(sizes.eds.dimension.3)",
	"&::-webkit-scrollbar": {
		width:
			"calc(var(--scrollbar-thumb-width) + var(--scrollbar-thumb-padding) * 2 + var(--scrollbar-track-border-width) * 2)",
	},
	"&::-webkit-scrollbar-track": {
		bg: "eds.fill.tertiary.select.default",
		borderInline: "var(--scrollbar-track-border-width) solid token(colors.eds.border.subtle)",
	},
	"&::-webkit-scrollbar-thumb": {
		bg: "transparent",
		boxShadow: "0 0 0 var(--scrollbar-thumb-width) token(colors.eds.icon.secondary) inset",
		border: "var(--scrollbar-thumb-padding) solid transparent",
		borderRadius: "100px",
	},
});

export const EulaAgreementDetails = ({
	title: pageTitle,
	eulaContentTitle,
	eulaContentBody,
	...rest
}: EulaAgreementDetailsProps): React.JSX.Element | null => {
	const title = pageTitle || eulaContentTitle;

	if (!eulaContentBody) return null;

	return (
		<div
			className={css({
				maxWidth: "960px",
				bg: "eds.background.elevated.high.default",
				borderRadius: "eds.dimension.24",
				borderColor: "eds.border.subtle",
				borderWidth: "1px",
				borderStyle: "solid",
			})}
		>
			<VStack alignItems="stretch" gap="0">
				<VStack
					alignItems="stretch"
					className={css({
						boxShadow: "eds.bottom.md",
						mdDown: {
							paddingBlock: "eds.dimension.16",
						},
						padding: "eds.dimension.24",
					})}
				>
					<CosmosText
						variant="headingLG"
						className={css({
							mdDown: {
								fontSize: "eds.font.16",
								letterSpacing: "0.32px",
							},
						})}
					>
						{title}
					</CosmosText>
				</VStack>
				<Box className={cx("eula-body", overflowStyles)} maxHeight="600px">
					<CosmosRichText html={eulaContentBody} className={css({ overflowWrap: "anywhere" })} />
				</Box>
				<VStack
					alignItems="stretch"
					className={css({
						boxShadow: "eds.top.md",
						smDown: {
							paddingBlock: "eds.dimension.16",
						},
						padding: "eds.dimension.24",
					})}
				>
					<EulaAgreementFooter {...rest} title={title} />
				</VStack>
			</VStack>
		</div>
	);
};
